// Lightweight i18n with auto-detection. Spanish is default; English if browser is non-Spanish.
import { createContext, useContext, useEffect, useState } from "react";
import React from "react";

const DICT = {
  es: {
    "nav.how": "Cómo funciona",
    "nav.ports": "Puertos",
    "nav.services": "Servicios",
    "nav.enter": "Entrar",
    "nav.start": "Empezar",
    "nav.faq": "Ayuda",
    "nav.legal": "Legal",
    "dash.owner.hello": "Hola",
    "dash.owner.desc": "Aquí gestionas tus solicitudes y ves las ofertas que te llegan.",
    "dash.owner.new": "Publicar solicitud",
    "dash.owner.yours": "Tus solicitudes",
    "dash.owner.nearby": "Profesionales cerca de tus barcos",
    "dash.owner.news": "Novedades",
    "dash.pro.hello": "Hola",
    "dash.pro.desc": "Estas son las solicitudes que encajan contigo. Sin buscarlas.",
    "req.status.open": "Abierta · esperando ofertas",
    "req.status.accepted": "Oferta aceptada",
    "req.status.in_progress": "En curso",
    "req.status.completed": "Completada",
    "req.pay": "Pagar",
    "req.offers": "Ofertas recibidas",
    "req.progress": "Progreso del trabajo",
    "req.chat": "Chat",
    "hero.badge": "El puerto digital de España",
    "hero.title1": "Cuidamos tu barco.",
    "hero.title2": "disfrutas del mar.",
    "hero.title2Prefix": "Tú",
    "hero.desc": "MarinaHub conecta a propietarios de embarcaciones con profesionales verificados en",
    "hero.desc2": "todos los puertos de España",
    "hero.desc3": ". Publica lo que necesitas, recibe ofertas en minutos y elige la que mejor te encaje. Sin comisiones. Sin complicaciones.",
    "hero.ctaOwner": "Soy propietario",
    "hero.ctaPro": "Soy profesional",
    "hero.verified": "Profesionales verificados",
    "hero.offers": "Ofertas en minutos",
    "hero.spain": "Toda España",
    "hero.rating": "4.9 media de profesionales",
    "hero.tag": "Del amarre al mar, sin llamar a nadie.",
    "hero.latest": "Última solicitud",
    "install.app": "Descarga la app",
    "install.subtitle": "Instala MarinaHub en tu móvil",
    "how.badge": "Cómo funciona",
    "how.title": "Un flujo pensado para dos partes.",
    "cta.title1": "Tu barco merece",
    "cta.title2": "un buen equipo.",
    "cta.desc": "Regístrate gratis. Recibes ofertas en minutos y decides tú, sin presiones.",
    "cta.register": "Crear cuenta",
    "footer": "MarinaHub · Hecho en España, con vista al mar.",
  },
  en: {
    "nav.how": "How it works",
    "nav.ports": "Marinas",
    "nav.services": "Services",
    "nav.enter": "Sign in",
    "nav.start": "Get started",
    "nav.faq": "Help",
    "nav.legal": "Legal",
    "dash.owner.hello": "Hi",
    "dash.owner.desc": "Manage your requests and see the offers coming in.",
    "dash.owner.new": "Post request",
    "dash.owner.yours": "Your requests",
    "dash.owner.nearby": "Professionals near your boats",
    "dash.owner.news": "Updates",
    "dash.pro.hello": "Hi",
    "dash.pro.desc": "These are the requests that match you. No searching needed.",
    "req.status.open": "Open · awaiting offers",
    "req.status.accepted": "Offer accepted",
    "req.status.in_progress": "In progress",
    "req.status.completed": "Completed",
    "req.pay": "Pay",
    "req.offers": "Offers received",
    "req.progress": "Job progress",
    "req.chat": "Chat",
    "hero.badge": "Spain's digital marina",
    "hero.title1": "We take care of your boat.",
    "hero.title2": "enjoy the sea.",
    "hero.title2Prefix": "You",
    "hero.desc": "MarinaHub connects boat owners with verified professionals in",
    "hero.desc2": "every marina across Spain",
    "hero.desc3": ". Post what you need, receive quotes in minutes and pick the one that fits you best. No fees. No hassle.",
    "hero.ctaOwner": "I'm a boat owner",
    "hero.ctaPro": "I'm a professional",
    "hero.verified": "Verified pros",
    "hero.offers": "Quotes in minutes",
    "hero.spain": "All Spain",
    "hero.rating": "4.9 average rating",
    "hero.tag": "From the dock to the sea, without a single call.",
    "hero.latest": "Latest request",
    "install.app": "Get the app",
    "install.subtitle": "Install MarinaHub on your phone",
    "how.badge": "How it works",
    "how.title": "A flow crafted for both sides.",
    "cta.title1": "Your boat deserves",
    "cta.title2": "a great crew.",
    "cta.desc": "Sign up for free. Receive offers in minutes and choose without pressure.",
    "cta.register": "Create account",
    "footer": "MarinaHub · Made in Spain, with an ocean view.",
  },
};

function detectLang() {
  // MarinaHub es una app para España. Español por defecto SIEMPRE.
  // Solo respetamos "en" si el usuario lo eligió manualmente desde el toggle.
  const stored = typeof localStorage !== "undefined" && localStorage.getItem("mh_lang");
  if (stored === "en") return "en";
  return "es";
}

const I18nCtx = createContext({ lang: "es", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLang());
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const setLang = (l) => { localStorage.setItem("mh_lang", l); setLangState(l); };
  const t = (k) => (DICT[lang] && DICT[lang][k]) || DICT.es[k] || k;
  return <I18nCtx.Provider value={{ lang, t, setLang }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
